import { Button, Col, Form, FormGroup, Input, InputGroup, InputGroupText, Label, Row } from 'reactstrap';
import ReactDatetime from 'react-datetime';
import moment from 'moment';
import isDirector from '../../../../helpers/isDirector';

interface NewHireStepTwoProps {
  currentUser: any;
  handleSubmitForm: (e: any) => void;
  currentSchool: any;
  setTitleId: (e: any) => void;
  titleId: string;
  titleList: any[];
  setHireDate: (e: any) => void;
  hireDate: string;
  setDepartmentId: (e: any) => void;
  departmentId: string;
  departmentList: any[];
  setSupervisorId: (e: any) => void;
  supervisorId: string;
  employees: any[];
  isIncludeOfferLetter: boolean | null | undefined;
  setIsIncludeOfferLetter: (value: boolean) => void;
  offerLetter: string;
  setOfferLetter: (value: string) => void;
  setNewEmployeeFormStep: (step: number) => void;
}

const NewHireStepTwo = ({
  currentUser,
  handleSubmitForm,
  currentSchool,
  setTitleId,
  titleId,
  titleList,
  setHireDate,
  hireDate,
  setDepartmentId,
  departmentId,
  departmentList,
  setSupervisorId,
  supervisorId,
  employees,
  isIncludeOfferLetter,
  setIsIncludeOfferLetter,
  offerLetter,
  setOfferLetter,
  setNewEmployeeFormStep,
}: NewHireStepTwoProps) => {
  const handleOfferLetterRadioChange = (include: boolean) => {
    setIsIncludeOfferLetter(include);
    if (!include) {
      setOfferLetter('');
    }
  };

  const employeesForSelect = isDirector(currentUser)
    ? employees?.filter((e) => e?.userId === currentUser?.id)
    : employees?.filter(
        (e) => e?.roles?.includes('admin') || e?.group?.name === 'Director' || e?.group?.name === 'UpperManagement',
      );
  return (
    <Form onSubmit={handleSubmitForm}>
      <Row className="d-flex align-items-center">
        <Col lg="3">
          <Label className="form-control-label" htmlFor="jobTitleSelect">
            Job Title:
          </Label>
        </Col>
        <Col lg="9">
          <Input
            id="jobTitleSelect"
            type="select"
            onChange={(e) => setTitleId(e.target.value)}
            value={titleId}
            required
          >
            <option value="">Select a title...</option>
            {titleList?.map((filter: any, index: number) => {
              return (
                <option value={filter.id} key={`title-${index}`}>
                  {filter.name}
                </option>
              );
            })}
          </Input>
        </Col>
      </Row>

      <Row className="d-flex align-items-center mt-4">
        <Col lg="3">
          <Label className="form-control-label">Work Location:</Label>
        </Col>
        <Col lg="9">
          <FormGroup check inline className="mr-3">
            <Label check htmlFor="work-location-company">
              <Input
                className="form-check-input"
                type="radio"
                name="workLocationRadio"
                id="work-location-company"
                checked={true}
                readOnly
              />
              Company Location
            </Label>
          </FormGroup>
          <Input className="mt-2" type="text" value={currentSchool?.label || 'N/A'} disabled readOnly />
        </Col>
      </Row>

      <Row className="d-flex align-items-center mt-4">
        <Col lg="3">
          <Label className="form-control-label" htmlFor="hireDateInput">
            Hire Date:
          </Label>
        </Col>
        <Col lg="9">
          <FormGroup className="custom-datepicker mb-0">
            <InputGroup className="input-group-alternative">
              <InputGroupText>
                <i className="ni ni-calendar-grid-58" />
              </InputGroupText>
              <ReactDatetime
                inputProps={{
                  placeholder: 'Select Hire Date',
                  id: 'hireDateInput',
                  required: true,
                }}
                onChange={(newValue) => {
                  if (moment.isMoment(newValue)) {
                    setHireDate(newValue.format('MM/DD/YYYY'));
                  } else if (typeof newValue === 'string') {
                    setHireDate(newValue);
                  }
                }}
                timeFormat={false}
                value={hireDate ? moment(hireDate, 'MM/DD/YYYY') : ''}
                dateFormat={'MM/DD/YYYY'}
                closeOnSelect={true}
              />
            </InputGroup>
          </FormGroup>
        </Col>
      </Row>

      <Row className="d-flex align-items-center mt-4">
        <Col lg="3">
          <Label className="form-control-label" htmlFor="departmentSelect">
            Department:
          </Label>
        </Col>
        <Col lg="9">
          <Input
            id="departmentSelect"
            type="select"
            onChange={(e) => setDepartmentId(e.target.value)}
            value={departmentId}
            required
          >
            <option value="">Select a department...</option>
            {departmentList?.map((filter: any, index: number) => {
              return (
                <option value={filter.id} key={`department-${index}`}>
                  {filter.name}
                </option>
              );
            })}
          </Input>
        </Col>
      </Row>

      <Row className="d-flex align-items-center mt-4">
        <Col lg="3">
          <Label className="form-control-label" htmlFor="supervisorSelect">
            Supervisor:
          </Label>
        </Col>
        <Col lg="9">
          <Input
            id="supervisorSelect"
            type="select"
            onChange={(e) => setSupervisorId(e.target.value)}
            value={supervisorId}
            required
          >
            <option value="">Select a supervisor...</option>
            {employeesForSelect
              ?.filter((e: any) => e?.status === 'ACTIVE')
              .map((filter: any, index: number) => {
                return (
                  <option value={filter.userId} key={`supervisor-${index}`}>
                    {`${filter?.firstName || ''} ${filter?.lastName || ''}`.trim()}
                  </option>
                );
              })}
          </Input>
        </Col>
      </Row>

      <Row className="d-flex align-items-center mt-4">
        <Col lg="3">
          <Label className="form-control-label">Offer Letter:</Label>
        </Col>
        <Col lg="9">
          <div className="d-flex flex-wrap">
            <FormGroup check inline className="mr-3">
              <Label check htmlFor="offer-letter-dont-include">
                <Input
                  className="form-check-input"
                  type="radio"
                  name="offer-letter-radio"
                  id="offer-letter-dont-include"
                  checked={isIncludeOfferLetter === false}
                  onChange={() => handleOfferLetterRadioChange(false)}
                  required
                />
                Don't include an offer letter
              </Label>
            </FormGroup>
            <FormGroup check inline className="mr-3">
              <Label check htmlFor="offer-letter-use-template">
                <Input
                  className="form-check-input"
                  type="radio"
                  name="offer-letter-radio"
                  id="offer-letter-use-template"
                  checked={isIncludeOfferLetter === true}
                  onChange={() => handleOfferLetterRadioChange(true)}
                  required
                />
                Use an offer letter template
              </Label>
            </FormGroup>
          </div>
          {isIncludeOfferLetter === true && (
            <Input
              className="mt-2"
              type="select"
              value={offerLetter}
              required={isIncludeOfferLetter === true}
              onChange={(e) => setOfferLetter(e.target.value)}
              id="offerLetterTemplateSelect"
            >
              <option value="">Select a template...</option>
              <option value="Management">Management Offer Letter</option>
              <option value="Staff">Staff Offer Letter</option>
            </Input>
          )}
        </Col>
      </Row>

      <hr />

      <div className="d-flex justify-content-end mt-4">
        <div>
          <Button className="btn-secondary mr-2" onClick={() => setNewEmployeeFormStep(0)}>
            Back
          </Button>
          <Button color="warning" type="submit">
            Continue
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default NewHireStepTwo;
